import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { utcToZonedTime } from 'date-fns-tz';

import { logError } from 'commons/logger';
import useScreen from 'hooks/useScreen';
import { IATA_LOS_ANGELES_TIMEZONE } from 'commons/constants';
import { decideLaterItem } from 'components/consumer/PDP/PDPShopTabsBar/itemContent';

const PDPSelectUpholsteryDecideLaterUI = ({ promotion, excludeFromSale }) => {
  const { isMobile } = useScreen();

  const { saleInfo, saleEnds, saleDisclaimer } = useMemo(() => {
    let promoEnds = '';
    let zonedEndDate = null;
    let formattedEndDate = null;

    if (promotion?.endDate) {
      try {
        zonedEndDate = utcToZonedTime(
          promotion.endDate,
          IATA_LOS_ANGELES_TIMEZONE
        );
      } catch (e) {
        logError(e, 'Error converting date to zoned time');
      }
      if (zonedEndDate && !excludeFromSale) {
        formattedEndDate = `${zonedEndDate.getMonth() +
          1}/${zonedEndDate.getDate()}`;
        promoEnds = `(Sale ends ${formattedEndDate})`;
      }
    }

    const tiers = promotion?.tiers;
    let promoInfo = `Can't decide?`;
    let promoDisclaimer = null;
    if (promotion?.tiers) {
      // means tiered discount.
      promoInfo = `Lock in up to ${
        promotion.tiers[promotion.tiers.length - 1].discount
      }%* off now.`;
      promoDisclaimer = tiers && tiers.length > 1 ? 'Save ' : null;
      const lastIdx = tiers && tiers.length - 1;
      if (tiers) {
        tiers.forEach((campaign, idx) => {
          const lastButOne = lastIdx - 1 === idx;
          // saleDisclaimer look like this - "Save 20% on orders $1499+, 25% on $2499+" and 30% on $3499+ or "Save 20% on Everything, 25% on $2499+ and 30% on $3499+"
          if (+idx === 0 && +campaign.min_cart === 0) {
            promoDisclaimer = promoDisclaimer.concat(
              `${campaign.discount}% on Everything, `
            );
          } else {
            promoDisclaimer = promoDisclaimer.concat(
              `${campaign.discount}% on ${idx === 0 ? 'orders' : ''} $${
                campaign.min_cart
              }+${lastButOne ? ' and ' : lastIdx === idx ? '' : ', '}`
            );
          }
        });
      }
    } else if (promotion?.discount) {
      promoInfo = `Lock in your ${promotion.discount}% discount.`;
    }

    return {
      saleInfo: promoInfo,
      saleEnds: promoEnds,
      saleDisclaimer: promoDisclaimer,
    };
  }, [promotion, excludeFromSale]);

  return (
    <div className="mt-8 mx-0 mb-12" id={decideLaterItem.contentId}>
      <div className="text-[1.1875rem] tracking-[.0125rem] font-bold leading-[1.75rem] text-gray mb-4">
        {!!saleInfo && (
          <span>
            {saleInfo}
            <br />
          </span>
        )}{' '}
        Choose your customizations later.
        {!!saleEnds && (
          <span>
            <br />
            {saleEnds}
            <br />
          </span>
        )}
      </div>

      <div className="text-sm text-gray [&_ol]:ml-[.9rem]">
        {!!saleDisclaimer && (
          <span className="PDPSelectUpholsteryDecideLater__content-sale-disclaimer">
            {`* ${saleDisclaimer}`}
            <br />
            <br />
          </span>
        )}
        <ol
          className={classnames(
            'PDPSelectUpholsteryDecideLater__content-list',
            {
              '!ml-0 space-y-4': isMobile,
            }
          )}
        >
          <li>Add to bag and checkout as normal.</li>
          <li>
            We’ll send you a free swatch kit which includes all of our
            upholstery options that you can select from.
          </li>
          <li>
            When you&apos;re ready, you can select your customizations from your
            account and we&apos;ll start building your furniture.
          </li>
        </ol>
      </div>
    </div>
  );
};

PDPSelectUpholsteryDecideLaterUI.propTypes = {
  promotion: PropTypes.object,
  excludeFromSale: PropTypes.bool,
};

export default PDPSelectUpholsteryDecideLaterUI;
